/**
 * Quote Service - Orchestrates quote workflows and validation
 */

import * as quoteRepository from "../repositories/quoteRepository.js";
import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Validate quote creation data
 * @param {Object} quoteData - Quote data to validate
 * @returns {Object} Validation result
 */
const validateQuoteCreation = (quoteData) => {
  const { leadId, cost, serviceType } = quoteData;
  const errors = [];

  if (!leadId) {
    errors.push("Lead ID is required");
  }

  if (!cost || cost <= 0) {
    errors.push("Cost must be greater than 0");
  }

  if (!serviceType) {
    errors.push("Service type is required");
  } else if (
    !["standard", "premium", "express", "packing", "storage"].includes(
      serviceType
    )
  ) {
    errors.push("Invalid service type");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Create quote from lead with cost information
 * @param {Object} quoteData - Quote creation data
 * @returns {Promise<Object>} Creation result
 */
export const createQuoteFromLead = async (quoteData) => {
  try {
    // Validate input
    const validation = validateQuoteCreation(quoteData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
    }

    // Use the conversion service to create quote from lead
    // Support both legacy date and new dates array
    const datesArray =
      quoteData.dates && quoteData.dates.length > 0
        ? quoteData.dates.filter((d) => d && d.trim() !== "")
        : quoteData.date
          ? [quoteData.date]
          : ["Not specified"];

    const result = await api.post("/conversion/lead-to-quote", {
      leadId: quoteData.leadId,
      quoteDetails: {
        from: quoteData.from || "Not specified",
        to: quoteData.to || "Not specified",
        date: datesArray[0] || quoteData.date || "Not specified", // Keep for backward compatibility
        dates: datesArray, // New: array of dates
        items: quoteData.items || [],
      },
    });

    if (result.success) {
      return {
        success: true,
        data: result.data,
        message: `Quote created successfully for R${quoteData.cost}`,
        quoteId: result.quoteId,
      };
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Send WhatsApp message with quote details
 * @param {Object} quote - Quote data
 * @returns {Promise<Object>} Send result
 */
const sendQuoteWhatsApp = async (quote) => {
  try {
    const messageData = {
      phone: quote.lead?.phone || quote.lead?.customerId,
      text: buildQuoteMessage(quote),
    };

    const result = await quoteRepository.sendWhatsAppMessage(messageData);

    return {
      success: true,
      message: "WhatsApp message sent successfully",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Build WhatsApp message for quote
 * @param {Object} quote - Quote data
 * @returns {string} Formatted message
 */
const buildQuoteMessage = (quote) => {
  const serviceTypeLabels = {
    standard: "Standard Moving",
    premium: "Premium Moving",
    express: "Express Moving",
    packing: "Packing & Moving",
    storage: "Storage & Moving",
  };

  let message = `🎉 *Great news! Your quote is ready!*\n\n`;
  message += `📋 *Service:* ${serviceTypeLabels[quote.serviceType] || quote.serviceType}\n`;
  message += `💰 *Estimated Cost:* R${quote.cost.toFixed(2)}\n`;
  message += `📅 *Valid Until:* ${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}\n\n`;

  if (quote.customMessage) {
    message += `💬 *Message:* ${quote.customMessage}\n\n`;
  }

  message += `To proceed with this quote:\n`;
  message += `• Reply *ACCEPT* to confirm\n`;
  message += `• Reply *DECLINE* if you'd like to discuss\n`;
  message += `• Reply *CALL* to speak with our team\n\n`;
  message += `Thank you for choosing Bubakii! 🚚`;

  return message;
};

/**
 * Get quotes with filters
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Quotes data
 */
export const getQuotes = async (filters = {}) => {
  try {
    const response = await api.get("/quotes", { params: filters });
    // Our fetch-based api client already returns parsed JSON
    // e.g., { success, quotes, count, message }
    return response;
  } catch (error) {
    logError("Error fetching quotes", {
      filters,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch quotes",
    };
  }
};

/**
 * Get a specific quote by ID
 * @param {string} quoteId - Quote identifier
 * @returns {Promise<Object>} Quote data
 */
export const getQuoteById = async (quoteId) => {
  try {
    const response = await api.get(`/quotes/${quoteId}`);
    // Return the parsed JSON object directly
    return response;
  } catch (error) {
    logError("Error fetching quote", {
      quoteId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch quote",
    };
  }
};

/**
 * Get quote by ID
 * @param {string} quoteId - Quote identifier
 * @returns {Promise<Object>} Quote data
 */
export const getQuote = async (quoteId) => {
  try {
    if (!quoteId) {
      throw new Error("Quote ID is required");
    }

    const quoteData = await quoteRepository.fetchQuote(quoteId);

    return {
      success: true,
      data: quoteData.data || quoteData,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
};

/**
 * Update quote status
 * @param {string} quoteId - Quote identifier
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateQuoteStatus = async (quoteId, updateData) => {
  try {
    const result = await quoteRepository.updateQuoteStatus(quoteId, updateData);

    return {
      success: true,
      data: result.data || result,
      message: "Quote status updated successfully",
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Send quote to customer
 * @param {string} quoteId - Quote identifier
 * @param {Object} sendData - Send data including message and estimated cost
 * @returns {Promise<Object>} Send result
 */
export const sendQuote = async (quoteId, sendData) => {
  try {
    if (!quoteId) {
      throw new Error("Quote ID is required");
    }

    const result = await quoteRepository.sendQuote(quoteId, sendData);

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Send failed",
      };
    }

    return {
      success: true,
      data: result.data,
      queued: result.queued,
      undoUntil: result.undoUntil,
      message:
        result.message ||
        (result.queued
          ? "Quote send scheduled"
          : "Quote sent successfully"),
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Cancel scheduled initial send (undo window).
 */
export const cancelQueuedQuoteSend = async (quoteId) => {
  return quoteRepository.cancelQueuedQuoteSend(quoteId);
};

/**
 * Update quote content
 * @param {string} quoteId - Quote identifier
 * @param {Object} updateData - Update data including contentUpdates and reason
 * @returns {Promise<Object>} Update result
 */
export const updateQuoteContent = async (
  quoteId,
  contentUpdates,
  reason = "Admin update"
) => {
  try {
    if (!quoteId) {
      throw new Error("Quote ID is required");
    }

    if (!contentUpdates || Object.keys(contentUpdates).length === 0) {
      throw new Error("No content updates provided");
    }

    const response = await api.patch(`/quotes/update/${quoteId}/content`, {
      contentUpdates,
      reason,
    });

    return response;
  } catch (error) {
    logError("Error updating quote content", {
      quoteId,
      contentUpdates,
      reason,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to update quote content",
    };
  }
};

/**
 * Resend quote to customer
 * @param {string} quoteId - Quote identifier
 * @param {Object} resendData - Resend data including message and estimated cost
 * @returns {Promise<Object>} Resend result
 */
export const resendQuote = async (quoteId, resendData) => {
  try {
    if (!quoteId) {
      throw new Error("Quote ID is required");
    }

    const response = await api.post(`/quotes/send/resend`, {
      quoteId,
      ...resendData,
    });

    return {
      success: true,
      data: response.data || response,
      message: "Quote resent successfully",
    };
  } catch (error) {
    logError("Error resending quote", {
      quoteId,
      resendData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to resend quote",
    };
  }
};

/**
 * Fetch upcoming unconverted opportunities for partner sharing
 * @param {Object} params - Query params (days, limit, includeTbc)
 * @returns {Promise<Object>}
 */
export const getUpcomingOpportunities = async (params = {}) => {
  try {
    const response = await api.get("/quotes/opportunities/upcoming", {
      params,
    });
    return {
      success: true,
      data: response.data || [],
      count: response.count ?? response.data?.length ?? 0,
      meta: response.meta || {},
    };
  } catch (error) {
    logError("Error fetching upcoming opportunities", {
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch upcoming opportunities",
      data: [],
      count: 0,
    };
  }
};

/**
 * Create a new quote
 * @param {Object} quoteData - Quote creation data
 * @returns {Promise<Object>} Creation result
 */
export const createQuote = async (quoteData, options = {}) => {
  try {
    const { skipAuth = false } = options;
    const endpoint = skipAuth ? "/quotes/customer/request" : "/quotes/create";
    const response = await api.post(endpoint, quoteData, {
      skipAuth,
      skipAuthRedirect: true,
    });
    return {
      success: true,
      data: response.data || response,
      quoteId: response.quoteId || response.data?.id,
      message: "Quote created successfully",
    };
  } catch (error) {
    logError("Error creating quote", {
      quoteData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to create quote",
    };
  }
};
